import '@elastic/react-search-ui-views/lib/styles/styles.css'
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  ListItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import {
  IconBuildingStore,
  IconCategory,
  IconMap,
  IconMapPin,
  IconPointFilled,
  IconSearch,
  IconToolsKitchen2,
} from '@tabler/icons-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ROUTES } from '../../helpers/routes-helper'
import {
  MOBILE_SEARCH_HEIGHT,
  WEB_NAV_HEIGHT,
} from '../../helpers/static-constants'
import useAppUtilities from '../../hooks/useAppUtilities'
import useElasticGlobalSearch from '../../hooks/useElasticGlobalSearch'
import { setSearchId } from '../../store/searchSlice'
import { logEvent } from '../../analytics/analytics'
import { useAppSelector } from '../../store'
import { selectLocation } from '../../store/authSlice'

const newSearchId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // fall through
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

interface RenderOptionProps extends React.HTMLAttributes<HTMLLIElement> {
  key: string
}

type ResultType = 'restaurant' | 'dish' | 'foodArea' | 'cuisine' | 'city'

// Intent-Intelligence keeps a single accent: the primary result type (restaurant) carries the
// signal wash; every other type reads as a neutral hairline tile, differentiated by icon shape
// rather than colour. Token literals mirror the light palette in `theme.ts`.
// How long the user must pause typing before a search_performed is logged, so a single query is one
// analytics row instead of one per keystroke.
const SEARCH_PERFORMED_IDLE_MS = 1200

const NEUTRAL_TILE = { bg: '#F1F0EB', color: '#565049' }
const RESULT_TYPE_CONFIG: Record<
  ResultType,
  { icon: React.ReactElement; bg: string; color: string }
> = {
  restaurant: {
    icon: <IconBuildingStore size={22} stroke={1.5} />,
    bg: '#FDF1EB',
    color: '#B23F14',
  },
  dish: {
    icon: <IconToolsKitchen2 size={22} stroke={1.5} />,
    ...NEUTRAL_TILE,
  },
  foodArea: {
    icon: <IconMapPin size={22} stroke={1.5} />,
    ...NEUTRAL_TILE,
  },
  cuisine: {
    icon: <IconCategory size={22} stroke={1.5} />,
    ...NEUTRAL_TILE,
  },
  city: {
    icon: <IconMap size={22} stroke={1.5} />,
    ...NEUTRAL_TILE,
  },
}

const ResultTypeIcon: React.FC<{ type: ResultType }> = ({ type }) => {
  const { icon, bg, color } = RESULT_TYPE_CONFIG[type]
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '8px',
        background: bg,
        border: '1px solid #D9D5CC',
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        mr: '10px',
      }}
    >
      {icon}
    </Box>
  )
}

const GROUP_LABELS: Record<string, string> = {
  restaurant: 'Restaurants',
  dish: 'Dishes',
  foodArea: 'Neighborhoods',
  cuisine: 'Cuisines',
  city: 'Cities',
}

// Stable group ordering so section headers render restaurant -> dish -> ... regardless of how MUI
// encounters options. The backend already returns groups in this order; this keeps it robust.
const GROUP_ORDER: Record<string, number> = {
  restaurant: 0,
  dish: 1,
  foodArea: 2,
  cuisine: 3,
  city: 4,
}

const optionType = (option: any): string => {
  if (option?.type) return String(option.type)
  if (option?.restaurantId) return 'restaurant'
  if (option?.dishID) return 'dish'
  if (option?.foodAreaId) return 'foodArea'
  if (option?.cityName) return 'city'
  return 'cuisine'
}

const GlobalSearch: React.FC = (): JSX.Element => {
  const { theme, dispatch, navigate } = useAppUtilities()
  const [searchInputValue, setSearchInputValue] = useState('')
  const eaterLocation = useAppSelector(selectLocation)
  const { results, loading } = useElasticGlobalSearch(
    searchInputValue,
    eaterLocation,
  )

  // Phase 0a — one searchId per SEARCH SESSION, reused across search_performed and every
  // search_result_selected, so query->click pairs are joinable in BigQuery (previously a fresh id
  // was minted per event, leaving typeahead selects with no matching search_performed). The session
  // ends when the input is cleared; continuous typing is one session.
  const searchIdRef = useRef<string | undefined>(undefined)
  const performedKeyRef = useRef<string>('')

  const ensureSearchId = useCallback((): string => {
    if (!searchIdRef.current) {
      const id = newSearchId()
      searchIdRef.current = id
      dispatch(setSearchId(id)) // expose to SearchResults so its selects share the session id
    }
    return searchIdRef.current
  }, [dispatch])

  const emitSearchPerformed = useCallback(
    (searchId: string, term: string, resultCount: number): void => {
      const key = `${searchId}::${term.trim()}`
      if (performedKeyRef.current === key) return // dedupe: one per (session, term)
      performedKeyRef.current = key
      logEvent('search_performed', {
        search_id: searchId,
        term,
        result_count: resultCount,
        screen_name: 'global_search',
      })
    },
    [],
  )

  // Reset the session when the box is cleared so the next search gets a fresh searchId.
  useEffect(() => {
    if (!searchInputValue.trim()) {
      searchIdRef.current = undefined
      performedKeyRef.current = ''
    }
  }, [searchInputValue])

  // Emit search_performed only once the user has SETTLED on a term — paused typing for
  // SEARCH_PERFORMED_IDLE_MS — not on every intermediate keystroke's debounced results. Firing on
  // each render logged the query character-by-character ("i","it","ita",…,"italian" = 6 rows for one
  // search), which polluted term analytics. Each keystroke/result update resets the timer; only the
  // settled term fires. Committed searches (Enter / picking a suggestion) emit immediately in
  // handleSubmit, so a fast select still logs. Deduped per (session, term) either way.
  useEffect(() => {
    const term = searchInputValue.trim()
    if (!term || loading) return
    const timer = setTimeout(() => {
      emitSearchPerformed(ensureSearchId(), term, results.length)
    }, SEARCH_PERFORMED_IDLE_MS)
    return () => clearTimeout(timer)
  }, [results, loading, searchInputValue, ensureSearchId, emitSearchPerformed])

  // City & cuisine suggestions now come from the backend (derived live from real restaurant data),
  // so the old synthetic city row is gone. We only re-sort by the stable type-group order so MUI's
  // `groupBy` renders one contiguous section per type. Render/select handlers read fields loosely,
  // so keep this list untyped as before.
  const processedResults = React.useMemo<any[]>(() => {
    const list = results ?? []
    return [...list].sort(
      (a, b) =>
        (GROUP_ORDER[optionType(a)] ?? 99) - (GROUP_ORDER[optionType(b)] ?? 99),
    )
  }, [results])

  const handleSubmit = (result: any): void => {
    if (!result) return

    // Free-text submit (Enter) — the "run a search" path, distinct from picking a suggestion.
    // Phase 2: navigate with the query only; SearchResults fetches real, paginated results from the
    // resactly-service /search proxy (it no longer replays a Redux snapshot).
    if (typeof result === 'string') {
      emitSearchPerformed(ensureSearchId(), result, results.length)
      navigate(
        `${ROUTES.SEARCH}?type=restaurant&q=${encodeURIComponent(result)}`,
      )
      return
    }

    // Suggestion picked: reuse the session searchId and record rank_position (0-based index in the
    // shown list) so query->item relevance is learnable. rank_position = -1 means not found (guard).
    const rankPosition = processedResults.findIndex((r) => r === result)
    const term = searchInputValue.trim()
    // Guarantee a matching search_performed for this committed search even if the user picked a
    // suggestion before the idle timer fired (deduped, so it never double-emits).
    emitSearchPerformed(ensureSearchId(), term, results.length)
    const data = result?._meta?.rawHit?._source

    if (data?.restaurantId && data?.alias) {
      logEvent('search_result_selected', {
        search_id: ensureSearchId(),
        restaurant_id: data.restaurantId as string,
        rank_position: rankPosition,
        term,
        result_type: 'restaurant',
        result_label: (result?.name?.raw as string) ?? undefined,
        screen_name: 'global_search',
      })
      navigate(
        `${ROUTES.RESTAURANT_DETAILS}/${data?.alias as string}/details`,
        { state: { source: 'search' } },
      )
    }
    if (data?.dishID && data?.restaurant) {
      logEvent('search_result_selected', {
        search_id: ensureSearchId(),
        dish_id: data.dishID as string,
        rank_position: rankPosition,
        term,
        result_type: 'dish',
        result_label: (result?.name?.raw as string) ?? undefined,
        screen_name: 'global_search',
      })
      navigate(
        `${ROUTES.RESTAURANT_DETAILS}/${data?.restaurant as string}/details`,
      )
    }
    if (data?.foodAreaId) {
      logEvent('search_result_selected', {
        search_id: ensureSearchId(),
        rank_position: rankPosition,
        term,
        result_type: 'foodArea',
        result_id: data.foodAreaId as string,
        result_label: (result?.name?.raw as string) ?? undefined,
        screen_name: 'global_search',
      })
      navigate(
        ROUTES.FOOD_AREA_RESTAURANTS.replace(
          ':foodAreaId',
          data.foodAreaId as string,
        ),
      )
    }
    if (data?.value && data?.key) {
      logEvent('search_result_selected', {
        search_id: ensureSearchId(),
        rank_position: rankPosition,
        term,
        result_type: 'cuisine',
        result_id: data.key as string,
        result_label: (data.key as string) ?? undefined,
        screen_name: 'global_search',
      })
      navigate(`${ROUTES.SEARCH}?type=cuisine&q=${data?.key as string}`)
    }
    if (result?.cityName && result?.value) {
      logEvent('search_result_selected', {
        search_id: ensureSearchId(),
        rank_position: rankPosition,
        term,
        result_type: 'city',
        result_id: result.value as string,
        result_label: result.cityName as string,
        screen_name: 'global_search',
      })
      navigate(`${ROUTES.SEARCH}?type=city&q=${result?.value as string}`)
    }
  }

  return (
    <Box
      sx={{
        borderLeft: { xs: 'none', md: `1px solid ${theme.palette.grey[300]}` },
        borderRight: `1px solid ${theme.palette.grey[300]}`,
        width: { xs: '100%', md: 'inherit' },
      }}
    >
      <Autocomplete
        freeSolo
        loading={loading}
        onChange={(event, value) => handleSubmit(value)}
        options={processedResults}
        inputValue={searchInputValue}
        onInputChange={(event, newInputValue) => {
          setSearchInputValue(newInputValue)
        }}
        filterOptions={(x) => x}
        groupBy={(option: any) =>
          GROUP_LABELS[optionType(option)] ?? 'Results'
        }
        handleHomeEndKeys
        getOptionLabel={(option: any) =>
          option?.name?.raw ?? option?.key?.raw ?? option?.cityName ?? ''
        }
        PaperComponent={(props) => (
          <>
            {processedResults && processedResults?.length > 0 && (
              <Paper
                {...props}
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  mt: { xs: '0px', md: '4px' },
                  boxShadow: {
                    xs: '0px 12px 20px 0px rgba(112, 112, 112, 0.25)',
                    md: '0px 0px 20px 0px rgba(112, 112, 112, 0.25)',
                  },
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: { xs: '0px', md: '8px' },
                  width: { xs: '100vw', md: '415px', lg: '100%' },
                }}
              />
            )}
          </>
        )}
        renderOption={(props: RenderOptionProps, option) => {
          const { key, ...otherProps } = props
          const keyId = option?.id?.raw ?? key

          return (
            <Box key={keyId} width="100%" px={'8px'}>
              {option?.cityName ? (
                <ListItem
                  {...otherProps}
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.grey[100],
                    },
                    padding: '8px !important',
                    borderRadius: '8px',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <ResultTypeIcon type="city" />
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="space-evenly"
                    >
                      <Typography
                        color={theme.palette.grey[800]}
                        variant="body2"
                        fontWeight={500}
                        textTransform={'capitalize'}
                      >
                        {option?.cityName}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              ) : (
                <ListItem
                  {...otherProps}
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.grey[100],
                    },
                    padding: '8px !important',
                    borderRadius: '8px',
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <ResultTypeIcon
                      type={
                        option?.restaurantId
                          ? 'restaurant'
                          : option?.dishID
                          ? 'dish'
                          : option?.foodAreaId
                          ? 'foodArea'
                          : 'cuisine'
                      }
                    />
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="space-evenly"
                    >
                      <Typography
                        color={theme.palette.grey[800]}
                        variant="body2"
                        fontWeight={500}
                      >
                        {option?.name?.raw ?? option?.key?.raw}
                      </Typography>
                      <Box display="flex">
                        <Typography
                          color={theme.palette.grey[500]}
                          variant="subtitle2"
                          fontWeight={500}
                        >
                          {option?.restaurantId
                            ? 'Restaurant '
                            : option?.dishID
                            ? 'Dish'
                            : option?.foodAreaId
                            ? 'Neighborhood'
                            : 'Cuisine'}
                        </Typography>
                        {option?.restaurantId && (
                          <>
                            <IconPointFilled
                              size={12}
                              stroke={2}
                              color={theme.palette.grey[300]}
                              style={{ marginLeft: '6px', alignSelf: 'center' }}
                            />
                            <IconMapPin
                              size={14}
                              stroke={2}
                              color={theme.palette.grey[600]}
                              style={{ marginLeft: '6px' }}
                            />
                            <Typography
                              variant="subtitle2"
                              component="p"
                              fontWeight={500}
                              sx={{ textTransform: 'capitalize' }}
                              color={theme.palette.grey[600]}
                              paddingLeft={'5px'}
                            >
                              {[
                                option?.address1?.raw,
                                option?.city?.raw,
                                option?.state?.raw,
                              ].join(', ')}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              )}
            </Box>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search for location, restaurant, cuisine or dish"
            variant="outlined"
            sx={{
              width: {
                xs: '100%',
                md: '100%',
                lg: '415px',
                xl: '415px',
              },
              minWidth: { md: '215px' },
              '& .MuiOutlinedInput-root': {
                fontSize: '16px',
                fontWeight: 500,
                height: {
                  xs: `${MOBILE_SEARCH_HEIGHT}px`,
                  md: `${WEB_NAV_HEIGHT}px`,
                },
                '& fieldset': {
                  border: 'none',
                },
              },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <IconSearch
                    stroke={2}
                    size={16}
                    style={{
                      color: theme.palette.grey[400],
                      marginLeft: '7px',
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params?.InputProps?.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  )
}

export default GlobalSearch
